import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import FinalCTA from "@/components/FinalCTA";
import ArmCards from "@/components/ArmCards";
import { COMPANY } from "@/lib/constants";
import ScrollRail from "@/components/ScrollRail";
import { PrinciplesRail } from "@/components/Rails";
import LottiePlayer from "@/components/LottiePlayer";
import networkAnim from "@/assets/lottie/network-nodes.json";

const PRINCIPLES = [
  ["Research before recommendation", "We do not propose a solution until the evidence supports one."],
  ["Accountability over activity", "Success is the outcome the business sees, not the number of tasks completed."],
  ["Selectivity over volume", "We take on fewer engagements so each receives senior attention."],
  ["Integration over fragmentation", "Systems, visibility and talent are planned together, not purchased separately."],
];

const Hero = () => {
  return (
    <section className="hp-hero-light hp-immersive" data-testid="about-hero" style={{ minHeight: "65vh" }}>
      <img 
        src="/images/about_corporate_1791101739162.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center 40%' }}
        alt="Corporate office" 
      />
      
      {/* Light gradient for text readability */}
      <div
        className="absolute inset-0 pointer-events-none bg-gradient-to-b lg:bg-gradient-to-r from-white via-white/95 to-white/10"
        aria-hidden="true"
      />
      <div className="hop-container relative z-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="hp-label text-[var(--hop-brand)]">About</p>
            <h1 className="hp-h1 mt-5">A selective operating partner for growing businesses.</h1>
            <p className="hp-lead mt-7" style={{ color: "#3c4768" }}>
              House of Projects is the operating brand of {COMPANY.legalName}, a Private Limited Company incorporated in India in 2026.
              It brings platforms and systems, social media and visibility, and hiring and talent acquisition under one accountable structure.
            </p>
          </Reveal>
        </div>
        <div className="hidden lg:block lg:col-span-4">
          <LottiePlayer className="hp-lottie-hero drop-shadow-xl opacity-90 mix-blend-multiply" data={networkAnim} label="One hub connected to three capabilities" />
        </div>
      </div>
      <div className="hp-rule absolute bottom-0 left-0 right-0 w-full" aria-hidden="true" />
    </section>
  );
};

const Why = () => (
  <section className="hp-section" aria-labelledby="about-why">
    <div className="hop-container grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <SectionHead label="Why we exist" title={<span id="about-why">Growth should not depend on managing six vendors.</span>} />
      </div>
      <Reveal delay={100} className="lg:col-span-7">
        <div className="space-y-5 hp-body max-w-2xl">
          <p>
            Scaling businesses typically spread marketing, technology, hiring, automation and content across separate suppliers.
            Leadership then becomes the integration layer between parties who never coordinate.
          </p>
          <p>
            House of Projects was set up to replace that arrangement with one operating layer: a team that researches the problem,
            diagnoses the constraint, and owns execution across the functions involved.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Selective = () => (
  <section className="hp-section hp-tint relative overflow-hidden bg-[#f7f7f5]" aria-labelledby="about-sel">
    {/* Background Image with strong readable gradient fade */}
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true">
      <img
        src="/images/about_corporate_1791101739162.jpg"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#f7f7f5] via-[#f7f7f5]/90 to-[#f7f7f5]/10" />
    </div>

    <div className="hop-container relative" style={{ zIndex: 10 }}>
      <SectionHead label="Selective engagement" title={<span id="about-sel">A limited roster, overseen by senior leadership.</span>}
        lead="We work with a limited number of clients at a time. Executive leadership stays involved from first diagnosis through ongoing optimisation." />
      <div className="mt-14">
        <ScrollRail inline={false} rail={(p) => <PrinciplesRail p={p} items={PRINCIPLES} />}>
          <ul>
            {PRINCIPLES.map(([t, d], i) => (
              <li key={t} className="hp-beat bg-white/40 p-6 rounded-lg backdrop-blur-md border border-slate-100/60 shadow-sm relative mb-4">
                <span className="hp-beat-n">0{i + 1}</span>
                <h3 className="font-display text-2xl font-semibold text-hop-blue mt-1">{t}</h3>
                <p className="hp-body text-[16px] mt-2 max-w-md">{d}</p>
              </li>
            ))}
          </ul>
        </ScrollRail>
      </div>
    </div>
  </section>
);

const Structure = () => (
  <section className="hp-section" aria-labelledby="about-struct">
    <div className="hop-container">
      <SectionHead label="One ecosystem" title={<span id="about-struct">Three capabilities under one brand.</span>}
        lead="House of Projects is the parent and operating brand. Arise & Echo and Applykar are its visibility and hiring capabilities." />
      <div className="mt-12"><ArmCards /></div>
      <Reveal>
        <p className="mt-10 hp-body max-w-3xl">
          Details of the company, its registrations and its offices are on the{" "}
          <Link to="/company-information" className="hp-link text-[var(--hop-brand)] inline-flex">company information page <ArrowRight className="arrow w-4 h-4" aria-hidden="true" /></Link>
        </p>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo title="About"
        description="House of Projects is the operating brand of HOUSEOFPROJECT TALENT AND BUSINESS MANAGEMENT PRIVATE LIMITED: a selective, senior-led operating partner across systems, visibility and hiring."
        path="/about" />
      <Hero /><Why /><Selective /><Structure />
      <FinalCTA />
    </>
  );
}
